import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import { useCallback, useEffect, useState } from "react";

import { useAtom } from "../../app-jotai";
import { openCloudDoc } from "../cloudActions";
import { cloudService } from "../cloudService";
import {
  cloudBrowserDialogAtom,
  versionHistoryDialogAtom,
} from "../dialogState";

import type { CloudDocSummary, CloudFolder } from "../types";

const directChildFolders = (
  folders: CloudFolder[],
  current: string,
): string[] => {
  const prefix = current ? `${current}/` : "";
  const names = new Set<string>();
  for (const f of folders) {
    if (current === "" ? !f.path.includes("/") : f.path.startsWith(prefix)) {
      const rest = f.path.slice(prefix.length);
      const name = rest.split("/")[0];
      if (name) {
        names.add(name);
      }
    }
  }
  return [...names].sort();
};

export const CloudBrowser = () => {
  const [open, setOpen] = useAtom(cloudBrowserDialogAtom);
  const [, setVersionDialog] = useAtom(versionHistoryDialogAtom);
  const [path, setPath] = useState("");
  const [docs, setDocs] = useState<CloudDocSummary[]>([]);
  const [folders, setFolders] = useState<CloudFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (p: string) => {
    setLoading(true);
    setError(null);
    try {
      const [d, f] = await Promise.all([
        cloudService.listDocs(p),
        cloudService.listFolders(),
      ]);
      setDocs(d);
      setFolders(f);
    } catch (e: any) {
      setError(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSelected(null);
      void refresh(path);
    }
  }, [open, path, refresh]);

  if (!open) {
    return null;
  }

  const close = () => setOpen(false);
  const subfolders = directChildFolders(folders, path);
  const breadcrumbs = path ? path.split("/") : [];

  const navTo = (p: string) => {
    setPath(p);
    setSelected(null);
  };

  const open_ = async (docId: string) => {
    try {
      await openCloudDoc(docId);
      close();
    } catch (e: any) {
      setError(e?.message || "Failed to open");
    }
  };

  const createFolder = async () => {
    const name = window.prompt("New folder name");
    if (!name) {
      return;
    }
    const newPath = path ? `${path}/${name.trim()}` : name.trim();
    await cloudService.createFolder(newPath);
    void refresh(path);
  };

  const deleteFolder = async (name: string) => {
    const fp = path ? `${path}/${name}` : name;
    const hasContents =
      docs.some(
        (d) => d.folderPath === fp || d.folderPath.startsWith(`${fp}/`),
      ) || folders.some((f) => f.path === fp || f.path.startsWith(`${fp}/`));
    const msg = hasContents
      ? `Folder "${name}" has contents. Delete the folder AND everything inside it?`
      : `Delete folder "${name}"?`;
    if (!window.confirm(msg)) {
      return;
    }
    await cloudService.deleteFolder(fp, true);
    void refresh(path);
  };

  const renameDoc = async (doc: CloudDocSummary) => {
    const name = window.prompt("Rename document", doc.name);
    if (!name || name === doc.name) {
      return;
    }
    await cloudService.renameDoc(doc.docId, name.trim());
    void refresh(path);
  };

  const moveDoc = async (doc: CloudDocSummary) => {
    const target = window.prompt(
      "Move to folder (path, blank = root)",
      doc.folderPath,
    );
    if (target === null) {
      return;
    }
    await cloudService.moveDoc(
      doc.docId,
      target.trim().replace(/^\/+|\/+$/g, ""),
    );
    void refresh(path);
  };

  const deleteDoc = async (doc: CloudDocSummary) => {
    if (
      !window.confirm(`Delete "${doc.name}"? This removes all its versions.`)
    ) {
      return;
    }
    await cloudService.deleteDoc(doc.docId);
    void refresh(path);
  };

  return (
    <Dialog size="wide" onCloseRequest={close} title="Open from cloud">
      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        {/* Breadcrumbs + actions */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            flexWrap: "wrap",
          }}
        >
          <button
            className="excalidraw-link"
            onClick={() => navTo("")}
            style={linkBtn}
          >
            Home
          </button>
          {breadcrumbs.map((seg, i) => {
            const p = breadcrumbs.slice(0, i + 1).join("/");
            return (
              <span key={p}>
                {" / "}
                <button onClick={() => navTo(p)} style={linkBtn}>
                  {seg}
                </button>
              </span>
            );
          })}
          <span style={{ marginLeft: "auto" }} />
          <FilledButton
            size="medium"
            variant="outlined"
            label="New folder"
            onClick={createFolder}
          />
        </div>

        {error && (
          <div style={{ color: "var(--color-danger)", fontSize: "0.85rem" }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ padding: "2rem", textAlign: "center" }}>Loading…</div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
              gap: "1rem",
              maxHeight: "58vh",
              overflowY: "auto",
              padding: "0.25rem",
            }}
          >
            {/* Folders */}
            {subfolders.map((name) => (
              <div key={`f-${name}`} style={card}>
                <div
                  style={{ ...thumb, cursor: "pointer", fontSize: "2.5rem" }}
                  onDoubleClick={() => navTo(path ? `${path}/${name}` : name)}
                  onClick={() => navTo(path ? `${path}/${name}` : name)}
                >
                  📁
                </div>
                <div style={cardTitle}>{name}</div>
                <button style={dangerLink} onClick={() => deleteFolder(name)}>
                  Delete
                </button>
              </div>
            ))}

            {/* Documents */}
            {docs.map((doc) => (
              <div
                key={doc.docId}
                style={{
                  ...card,
                  outline:
                    selected === doc.docId
                      ? "2px solid var(--color-primary)"
                      : "none",
                }}
                onClick={() => setSelected(doc.docId)}
                onDoubleClick={() => open_(doc.docId)}
              >
                {doc.thumbnailUrl ? (
                  <img src={doc.thumbnailUrl} alt={doc.name} style={thumb} />
                ) : (
                  <div
                    style={{ ...thumb, background: "var(--color-gray-10)" }}
                  />
                )}
                <div style={cardTitle}>{doc.name}</div>
                <div
                  style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}
                >
                  <button style={miniLink} onClick={() => open_(doc.docId)}>
                    Open
                  </button>
                  <button
                    style={miniLink}
                    onClick={() =>
                      setVersionDialog({
                        isOpen: true,
                        docId: doc.docId,
                        name: doc.name,
                      })
                    }
                  >
                    History
                  </button>
                  <button style={miniLink} onClick={() => renameDoc(doc)}>
                    Rename
                  </button>
                  <button style={miniLink} onClick={() => moveDoc(doc)}>
                    Move
                  </button>
                  <button style={dangerLink} onClick={() => deleteDoc(doc)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}

            {!subfolders.length && !docs.length && (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: "2rem",
                }}
              >
                This folder is empty.
              </div>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
};

const linkBtn: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "var(--color-primary)",
  cursor: "pointer",
  padding: 0,
  font: "inherit",
};
const card: React.CSSProperties = {
  border: "1px solid var(--color-gray-30)",
  borderRadius: "0.5rem",
  padding: "0.5rem",
  display: "flex",
  flexDirection: "column",
  gap: "0.35rem",
};
const thumb: React.CSSProperties = {
  width: "100%",
  height: 110,
  objectFit: "contain",
  background: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const cardTitle: React.CSSProperties = {
  fontSize: "0.8rem",
  fontWeight: 600,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const miniLink: React.CSSProperties = { ...linkBtn, fontSize: "0.72rem" };
const dangerLink: React.CSSProperties = {
  ...miniLink,
  color: "var(--color-danger)",
};
